import { useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { cn } from './cn.js';
import { Icon } from './Icon.js';
import { Popover, PopoverContent, PopoverTrigger } from './primitives/popover.js';

export interface ComboboxOption {
  value: string;
  /** O que o gatilho escreve depois da escolha — a placa, o nome. */
  label: string;
  /** A linha de apoio na lista: o modelo e o dono, o e-mail. */
  detail?: ReactNode;
}

export interface ComboboxProps {
  label: string;
  /** A opção escolhida — inteira, porque o gatilho escreve o rótulo dela. */
  value: ComboboxOption | null;
  onChange: (option: ComboboxOption | null) => void;
  /**
   * A busca mudou. Quem chama busca no servidor (com o atraso que quiser) e
   * devolve o resultado em `options` — o componente não filtra nada sozinho,
   * porque a lista inteira não cabe na tela: são 76 mil placas numa praça.
   */
  onSearch: (term: string) => void;
  /** O resultado da busca ATUAL. */
  options: readonly ComboboxOption[];
  loading?: boolean;
  /**
   * Ajusta o que a pessoa digita enquanto digita — a placa sai em maiúscula e
   * sem hífen, e quem digitou "abc-1d" vê "ABC1D", que é o que a busca casa.
   */
  normalize?: (term: string) => string;
  /** Abaixo disto a busca não sai, e a lista diz quantos faltam. */
  minLength?: number;
  placeholder?: string;
  searchPlaceholder?: string;
  /** Quando a busca volta vazia. */
  emptyMessage?: string;
  /**
   * O estado que NÃO é lista: "os veículos desta base não foram
   * sincronizados". Vence o resultado, porque uma lista vazia ali seria "não
   * há" onde a verdade é "não sei".
   */
  notice?: ReactNode;
  help?: ReactNode;
  disabled?: boolean;
}

/**
 * O próximo item ativo da lista ao apertar uma seta — dá a volta nas pontas.
 * `-1` é "nenhum", e é de onde a primeira seta para baixo parte.
 */
export function moveActive(current: number, delta: 1 | -1, length: number): number {
  if (length === 0) return -1;
  if (current === -1) return delta === 1 ? 0 : length - 1;
  return (current + delta + length) % length;
}

/**
 * Escolher UM entre muitos, buscando no servidor.
 *
 * É o irmão de `MultiSelect`, e existe pelo motivo inverso: aquele filtra uma
 * lista que já está na mão; este pergunta ao servidor a cada busca, porque a
 * lista inteira não vem — o espelho de veículos de uma praça, o cadastro de
 * pessoas. Nasceu na abertura de demanda (`demandas-46`): a placa e o
 * solicitante são o mesmo gesto, "procure e escolha um".
 *
 * A camada (abrir, fechar, devolver o foco) é do `Popover`, como no
 * `MultiSelect`; a busca, a lista e o teclado são daqui. Setas movem, Enter
 * escolhe, Esc fecha — o padrão de `combobox` da WAI-ARIA, com
 * `aria-activedescendant` para o leitor de tela acompanhar o item ativo sem o
 * foco sair do campo de busca.
 *
 * **O campo é opcional por construção**: escolhido, ele ganha "Limpar" ao lado.
 * Tirar a escolha não pode depender de achar a opção de novo na lista.
 */
export function Combobox({
  label,
  value,
  onChange,
  onSearch,
  options,
  loading = false,
  normalize,
  minLength = 2,
  placeholder = 'Nenhum',
  searchPlaceholder = 'Buscar…',
  emptyMessage = 'Nada encontrado.',
  notice,
  help,
  disabled = false,
}: ComboboxProps) {
  const idLabel = useId();
  const idTrigger = useId();
  const idList = useId();
  const idHelp = useId();
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [active, setActive] = useState(-1);
  const searchRef = useRef<HTMLInputElement>(null);

  const short = term.trim().length < minLength;

  const search = (raw: string) => {
    const next = normalize ? normalize(raw) : raw;
    setTerm(next);
    setActive(-1);
    onSearch(next.trim());
  };

  const choose = (option: ComboboxOption) => {
    onChange(option);
    setOpen(false);
    setTerm('');
  };

  const shown = short || notice ? [] : options;

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setActive(moveActive(active, e.key === 'ArrowDown' ? 1 : -1, shown.length));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const option = shown[active] ?? (shown.length === 1 ? shown[0] : undefined);
      if (option) choose(option);
    }
  };

  return (
    <div className="flex flex-col gap-1.5">
      <span id={idLabel} className="text-xs font-medium text-texto">
        {label}
      </span>

      <div className="flex items-center gap-2">
        <Popover
          open={open}
          onOpenChange={(next) => {
            if (disabled) return;
            setOpen(next);
            if (!next) {
              setTerm('');
              setActive(-1);
            }
          }}
        >
          <PopoverTrigger asChild>
            <button
              /* Veste um campo: o foco dele é o de dentro, não o anel do botão. */
              data-campo=""
              type="button"
              id={idTrigger}
              disabled={disabled}
              aria-labelledby={`${idLabel} ${idTrigger}`}
              aria-describedby={help ? idHelp : undefined}
              className={cn(
                'flex h-9 min-w-0 flex-1 items-center justify-between gap-2 rounded-borda border bg-superficie px-3 text-sm',
                'text-texto pointer-coarse:h-11',
                'transition-[border-color,background-color] active:bg-superficie-suave',
                'border-borda-forte hover:border-texto-suave',
                'disabled:cursor-not-allowed disabled:bg-superficie-suave disabled:opacity-60',
              )}
            >
              <span className={cn('truncate text-left', !value && 'text-texto-suave')}>
                {value?.label ?? placeholder}
              </span>
              <Icon nome="busca" size={16} className="shrink-0 text-texto-suave" />
            </button>
          </PopoverTrigger>

          <PopoverContent
            align="start"
            /* `z-10`: o mesmo motivo do `MultiSelect` — ver lá. */
            className="z-10 w-[min(24rem,calc(100vw-2rem))] p-0"
            onOpenAutoFocus={(e) => {
              e.preventDefault();
              searchRef.current?.focus();
            }}
          >
            <div className="border-b border-borda p-2">
              <div className="relative">
                <Icon
                  nome="busca"
                  size={16}
                  className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-texto-suave"
                />
                <input
                  ref={searchRef}
                  type="text"
                  role="combobox"
                  aria-expanded={open}
                  aria-controls={idList}
                  aria-autocomplete="list"
                  aria-label={`Buscar ${label.toLowerCase()}`}
                  aria-activedescendant={active >= 0 ? `${idList}-${active}` : undefined}
                  value={term}
                  onChange={(e) => search(e.target.value)}
                  onKeyDown={onKeyDown}
                  placeholder={searchPlaceholder}
                  autoComplete="off"
                  spellCheck={false}
                  className={cn(
                    'h-9 w-full rounded-borda border border-borda-forte bg-superficie pl-8 pr-2 text-sm',
                    'text-texto placeholder:text-texto-suave pointer-coarse:h-11',
                  )}
                />
              </div>
            </div>

            <ul id={idList} role="listbox" aria-labelledby={idLabel} className="max-h-72 overflow-y-auto p-1">
              {notice && <li className="px-2 py-3 text-sm text-texto-suave">{notice}</li>}
              {!notice && short && (
                <li className="px-2 py-3 text-center text-sm text-texto-suave">
                  Digite ao menos {minLength} caracteres.
                </li>
              )}
              {!notice && !short && loading && (
                <li className="px-2 py-3 text-center text-sm text-texto-suave">Buscando…</li>
              )}
              {!notice && !short && !loading && shown.length === 0 && (
                <li className="px-2 py-3 text-center text-sm text-texto-suave">{emptyMessage}</li>
              )}
              {!loading &&
                shown.map((option, i) => (
                  <li
                    key={option.value}
                    id={`${idList}-${i}`}
                    role="option"
                    aria-selected={value?.value === option.value}
                    onMouseEnter={() => setActive(i)}
                    /* `onMouseDown` e não `onClick`: o clique chega depois do
                       `blur` do campo, e o popover já teria fechado */
                    onMouseDown={(e) => {
                      e.preventDefault();
                      choose(option);
                    }}
                    className={cn(
                      'flex min-h-11 cursor-pointer flex-col justify-center gap-0.5 rounded-borda px-2 py-1.5',
                      i === active ? 'bg-superficie-suave' : 'hover:bg-superficie-suave',
                    )}
                  >
                    <span className="truncate text-sm font-semibold text-texto">{option.label}</span>
                    {option.detail && (
                      <span className="truncate text-xs text-texto-suave">{option.detail}</span>
                    )}
                  </li>
                ))}
            </ul>
          </PopoverContent>
        </Popover>

        {value && !disabled && (
          <button
            type="button"
            onClick={() => onChange(null)}
            aria-label={`Limpar ${label.toLowerCase()}`}
            className="h-9 shrink-0 rounded-borda px-2 text-xs font-semibold text-texto-suave hover:bg-superficie-suave hover:text-texto pointer-coarse:h-11"
          >
            Limpar
          </button>
        )}
      </div>

      {help && (
        <p id={idHelp} className="text-xs text-texto-suave">
          {help}
        </p>
      )}
    </div>
  );
}
